import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
} from 'recharts';
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer';
import { getCompetenciesTrendSummary } from '../../requests';
import { useDashboardFilters } from './context/DashboardContext';
import ResponseLoader from '../common/ResponseLoader';
import styles from '../../_css/ReportingAndSterring.module.css';

const CompetencyTrends = () => {
    const dispatch = useDispatch();
    const { filters } = useDashboardFilters();
    const [loading, setLoading] = useState(false);

    const [summaryTableData, setSummaryTableData] = useState([]);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                dispatch(startLoader());
                const startDate = filters.dateRange.from;
                const endDate = filters.dateRange.to;
                const companyName = filters.company;
                const res = await getCompetenciesTrendSummary({ startDate, endDate, companyName });
                // console.log("Response from competencies API: ", res);

                if (res.data.success && res.data.data) {
                    const { summary, chart } = res.data.data;

                    setSummaryTableData(summary?.length ? summary : []);
                    setChartData(chart?.length ? chart : []);
                } else {
                    // No fallback to static data
                    setSummaryTableData([]);
                    setChartData([]);
                }
            } catch (err) {
                console.error("Error fetching competency trends:", err);
                // No fallback to static data
                setSummaryTableData([]);
                setChartData([]);
            } finally {
                setLoading(false); // Stop loading
                dispatch(stopLoader());
            }
        };

        fetchData();
    }, [filters, dispatch]);

    return (
        <div className={styles.competencyTrendsContainer}>
            <div>
                <h5 className={styles.competencyTrendsTitle}>
                    Competency Trends
                </h5>
            </div>

            {loading ? (
                // Show loader when loading
                <div className={styles.loaderContainer}>
                    <ResponseLoader />
                </div>
            ) : (
                // Show content when not loading
                <div className={styles.contentContainer}>
                    {/* Check if we have data */}
                    {summaryTableData.length === 0 || chartData.length === 0 ? (
                        // Show no data message
                        <div className={styles.noDataContainer}>
                            <div className={styles.noDataContent}>

                                <p className={styles.noDataTitle}>
                                    No Data to show
                                </p>
                                <p className={styles.noDataSubtitle}>
                                    Competency trends data is not available
                                </p>
                            </div>
                        </div>
                    ) : (
                        // Show data when available
                        <>
                            {/* Left: Table */}
                            <div className={styles.tableContainer}>
                                <div className={styles.tableWrapper}>
                                    <table className={styles.competencyTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.tableHeaderCompetency}>Competency</th>
                                                <th className={styles.tableHeaderScore}>Avg Score</th>
                                                <th className={styles.tableHeaderTrend}>Trend (Last 3 months)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summaryTableData.map((comp) => (
                                                <tr key={comp.name}>
                                                    <td className={styles.tableCell}>
                                                        <span
                                                            className={styles.colorDot}
                                                            style={{ backgroundColor: comp.color }}
                                                        />
                                                        {comp.name}
                                                    </td>
                                                    <td className={styles.tableCell}>{comp.avgScore}</td>
                                                    <td className={styles.trendCell}>
                                                        {parseFloat(comp.trend) > 0 ? (
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/img/upward.png`}
                                                                alt="Upward Trend"
                                                                className={styles.trendIcon}
                                                            />
                                                        ) : parseFloat(comp.trend) < 0 ? (
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/img/downward.png`}
                                                                alt="Downward Trend"
                                                                className={styles.trendIcon}
                                                            />
                                                        ) : (
                                                            <span className={styles.neutralTrend}>➖</span>
                                                        )}
                                                        {comp.trend}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Right: Chart */}
                            <div className={styles.chartContainer}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                                        <CartesianGrid stroke="#E5E7EB" vertical={false} />
                                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={false} />
                                        <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{ fill: "#6B7280", fontSize: 12 }} />
                                        {summaryTableData.map((comp) => (
                                            <Line key={comp.dataKey} type="linear" dataKey={comp.dataKey} stroke={comp.color} strokeWidth={3} dot={false} activeDot={{ r: 6 }} name={comp.name} />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CompetencyTrends;